<script lang="ts">
import { computed, nextTick, ref, toRef, watch, type Component } from 'vue';

import { useKeydown } from '@sg-core/resources/assets/js/composables';
import { getCurrentlyFocusableElements, preloadImages } from '@sg-core/resources/assets/js/utils';
import { PhxPicture } from '@sg-core/src/View/Components/Atoms/Picture';

import PhxsgLightboxBackgroundVideoItem, {
  type BackgroundVideoMountPayload,
} from './phxsg-lightbox-background-video-item.vue';
import PhxsgLightboxVideoItem from './phxsg-lightbox-video-item.vue';
import { useLightbox } from './use-lightbox';
import { usePictureZoom, type LightboxCursor } from './use-picture-zoom';

export interface LightboxPictureSource {
  srcset: { path: string; size?: string }[] | string;
  width?: number;
  height?: number;
  media?: string;
}

export interface LightboxPictureImage {
  src: string;
  srcset?: string;
  width?: number;
  height?: number;
}

export interface LightboxPictureData {
  alt?: string;
  sources: LightboxPictureSource[];
  image: LightboxPictureImage;
  caption?: string | null;
}

export interface LightboxVideoSource {
  src: string;
  type?: string;
}

export interface LightboxBackgroundVideoData {
  poster?: string | null;
  sources: LightboxVideoSource[];
  caption?: string | null;
}

export interface LightboxVideoData extends LightboxBackgroundVideoData {
  autoplay?: boolean;
}

export type LightboxCustomData = Record<string, unknown>;

export interface LightboxBaseItem<TType extends string = string, TData = Record<string, unknown>> {
  type: TType;
  data: TData;
  videoTracking?: Record<string, unknown>;
}

export type LightboxPictureItem = LightboxBaseItem<'picture', LightboxPictureData>;
export type LightboxBackgroundVideoItem = LightboxBaseItem<'background-video', LightboxBackgroundVideoData>;
export type LightboxVideoItem = LightboxBaseItem<'video', LightboxVideoData>;
export type LightboxCustomItem = LightboxBaseItem<string, LightboxCustomData>;
export type LightboxItem = LightboxPictureItem | LightboxBackgroundVideoItem | LightboxVideoItem | LightboxCustomItem;

export interface LightboxI18n {
  close?: { label?: string };
  navPrev?: { label?: string };
  navNext?: { label?: string };
  backgroundVideo?: { play?: string; pause?: string };
  tooltip?: { label?: string };
  zoom?: { in?: string; out?: string };
}

export interface LightboxComponents {
  videoPlayerComponent?: Component;
  backgroundVideoComponent?: Component;
}

export interface PhxsgLightboxProps {
  id: string;
  items: LightboxItem[];
  loop?: boolean;
  teleportTarget?: string | null;
  i18n?: LightboxI18n;
  components?: LightboxComponents;
  cursor?: LightboxCursor;
  steps?: number;
  counter?: boolean;
  bullets?: boolean;
  modelValue?: number | null;
  zoomImageOnly?: boolean;
}

export function isPictureItem(item: LightboxItem): item is LightboxPictureItem {
  return item.type === 'picture';
}

export function isBackgroundVideoItem(item: LightboxItem): item is LightboxBackgroundVideoItem {
  return item.type === 'background-video';
}

export function isVideoItem(item: LightboxItem): item is LightboxVideoItem {
  return item.type === 'video';
}

export type { BackgroundVideoMountPayload, LightboxCursor };
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  id,
  items,
  loop = false,
  teleportTarget = null,
  i18n = {},
  components = {},
  cursor = {},
  steps = 3,
  counter = false,
  bullets = false,
  modelValue = null,
  zoomImageOnly = false,
} = defineProps<PhxsgLightboxProps>();

const emit = defineEmits<{
  'update:modelValue': [index: number];
  change: [index: number];
  open: [index: number];
  close: [];
}>();

const containerRef = ref<HTMLElement | null>(null);
const itemOffset = ref(0);
const pictureZoomContainerRef = ref<HTMLElement | null>(null);
const pictureZoomElementRef = ref<HTMLElement | null>(null);

const SWIPE_DISTANCE_THRESHOLD = 50;
const SWIPE_AXIS_RATIO = 1.5;
const SWIPE_INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="button"], [data-lightbox-ignore]';
const PICTURE_ZOOM_SELECTOR = '.phxsg-lightbox__zoom-container';

let swipePointerId: number | null = null;
let swipeStartX = 0;
let swipeStartY = 0;

const itemCount = computed(() => items.length);
const loopRef = toRef(() => loop);
const modelValueRef = toRef(() => modelValue);

const { isOpen, currentIndex, open, close, goTo, canGoPrev, canGoNext } = useLightbox({
  id,
  itemCount,
  loop: loopRef,
  modelValue: modelValueRef,
  onOpen: () => emit('open', currentIndex.value),
  onClose: () => emit('close'),
});

const currentItem = computed<LightboxItem | null>(() => {
  if (currentIndex.value < 0 || currentIndex.value >= items.length) {
    return null;
  }

  return items[currentIndex.value];
});

const currentPictureData = computed(() => {
  if (!currentItem.value || !isPictureItem(currentItem.value) || !currentItem.value.data.image.src) {
    return null;
  }

  return {
    ...currentItem.value.data,
    sources: currentItem.value.data.sources.map((source) => ({
      ...source,
      width: source.width ?? 1,
      height: source.height ?? 1,
    })),
    image: {
      ...currentItem.value.data.image,
      width: currentItem.value.data.image.width ?? 1,
      height: currentItem.value.data.image.height ?? 1,
    },
  };
});

const isPictureActive = computed(() => isOpen.value && currentPictureData.value !== null);

const counterText = computed(() => {
  if (!counter || items.length <= 1) {
    return null;
  }

  return `${currentIndex.value + 1}/${items.length}`;
});

const teleportTo = computed(() => {
  if (teleportTarget) {
    return `#${teleportTarget}`;
  }

  return 'body';
});

const {
  isZoomed,
  currentCursor,
  toggleZoom,
  resetZoom,
  resetZoomState,
  zoomIn,
  zoomOut,
  zoomStep,
  scalePercent,
  canZoomIn,
  canZoomOut,
} = usePictureZoom({
  containerRef: pictureZoomContainerRef,
  elementRef: pictureZoomElementRef,
  enabled: isPictureActive,
  cursor,
  steps,
});

const pointerOnImage = ref(false);

function isPointerOnRenderedImage(clientX: number, clientY: number): boolean {
  const img = pictureZoomElementRef.value?.querySelector<HTMLImageElement>('.phxsg-lightbox__image');

  if (!img) {
    return false;
  }

  const box = img.getBoundingClientRect();
  const { naturalWidth, naturalHeight } = img;

  if (!naturalWidth || !naturalHeight) {
    return clientX >= box.left && clientX <= box.right && clientY >= box.top && clientY <= box.bottom;
  }

  const scale = Math.min(box.width / naturalWidth, box.height / naturalHeight);
  const renderedWidth = naturalWidth * scale;
  const renderedHeight = naturalHeight * scale;
  const left = box.left + (box.width - renderedWidth) / 2;
  const top = box.top + (box.height - renderedHeight) / 2;

  return clientX >= left && clientX <= left + renderedWidth && clientY >= top && clientY <= top + renderedHeight;
}

function handlePictureClick(event: MouseEvent): void {
  if (zoomImageOnly && !isPointerOnRenderedImage(event.clientX, event.clientY)) {
    return;
  }

  toggleZoom(event);
}

function handlePicturePointerMove(event: PointerEvent): void {
  if (!zoomImageOnly) {
    return;
  }

  pointerOnImage.value = isPointerOnRenderedImage(event.clientX, event.clientY);
}

function handlePicturePointerLeave(): void {
  pointerOnImage.value = false;
}

function handleBackgroundVideoMounted(payload: BackgroundVideoMountPayload): void {
  const player = payload?.player?.value;

  if (!player) {
    return;
  }

  if (typeof player.show === 'function') {
    player.show();
    return;
  }

  if (typeof player.play === 'function') {
    player.play();
  }
}

watch(currentIndex, (value) => {
  resetZoomState();
  emit('update:modelValue', value);
  emit('change', value);
  preloadAdjacent(value);
});

watch(isOpen, async (value) => {
  if (value) {
    resetZoom();
    void focusInitialElement();
    preloadAdjacent(currentIndex.value);
  }
});

function handleOverlayClick(): void {
  close();
}

async function focusInitialElement(): Promise<void> {
  await nextTick();
  await waitForNextFrame();
  focusInitialElementIfNeeded();

  await waitForNextFrame();
  focusInitialElementIfNeeded();
}

function focusInitialElementIfNeeded(): void {
  const container = containerRef.value;

  if (!container) {
    return;
  }

  const activeElement = document.activeElement;

  if (activeElement && container.contains(activeElement) && activeElement !== container) {
    return;
  }

  const firstFocusableElement = getCurrentlyFocusableElements(container)[0];
  (firstFocusableElement ?? container).focus({ preventScroll: true });
}

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function handleLightboxKeydown(event: KeyboardEvent): void {
  if (!isOpen.value || event.key !== 'Tab') {
    return;
  }

  const container = containerRef.value;

  if (!container) {
    return;
  }

  const focusableElements = getCurrentlyFocusableElements(container);

  if (focusableElements.length === 0) {
    event.preventDefault();
    container.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (focusableElements.length === 1) {
    event.preventDefault();
    firstElement.focus();
    return;
  }

  if (event.shiftKey && (activeElement === firstElement || activeElement === container)) {
    event.preventDefault();
    lastElement.focus();
    return;
  }

  if (!event.shiftKey && (!activeElement || !container.contains(activeElement) || activeElement === lastElement)) {
    event.preventDefault();
    firstElement.focus();
  }
}

function handleSwipePointerDown(event: PointerEvent): void {
  if (event.pointerType !== 'touch' || items.length <= 1 || isZoomed.value) {
    return;
  }

  if (swipePointerId !== null) {
    resetSwipePointer();
    return;
  }

  if (isInteractiveSwipeTarget(event.target)) {
    return;
  }

  swipePointerId = event.pointerId;
  swipeStartX = event.clientX;
  swipeStartY = event.clientY;
}

function handleSwipePointerUp(event: PointerEvent): void {
  if (event.pointerId !== swipePointerId) {
    return;
  }

  const dx = event.clientX - swipeStartX;
  const dy = event.clientY - swipeStartY;

  resetSwipePointer();

  if (isZoomed.value || Math.abs(dx) < SWIPE_DISTANCE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * SWIPE_AXIS_RATIO) {
    return;
  }

  if (dx < 0) {
    goNext();
    return;
  }

  goPrev();
}

function handleLightboxTouchMove(event: TouchEvent): void {
  if (event.touches.length < 2 || !shouldPreventLightboxPinch(event.target)) {
    return;
  }

  if (event.cancelable) {
    event.preventDefault();
  }
}

function handleLightboxGesture(event: Event): void {
  if (!shouldPreventLightboxPinch(event.target)) {
    return;
  }

  event.preventDefault();
}

function shouldPreventLightboxPinch(target: EventTarget | null): boolean {
  if (!isOpen.value) {
    return false;
  }

  if (!isPictureActive.value) {
    return true;
  }

  return !(target instanceof Element && Boolean(target.closest(PICTURE_ZOOM_SELECTOR)));
}

function resetSwipePointer(): void {
  swipePointerId = null;
}

function isInteractiveSwipeTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(SWIPE_INTERACTIVE_SELECTOR));
}

function preloadAdjacent(index: number): void {
  const neighbours = [
    index - 1,
    index + 1,
    loop && index === 0 ? items.length - 1 : -1,
    loop && index === items.length - 1 ? 0 : -1,
  ];
  const preloadItems: LightboxPictureData[] = [];

  for (const neighbourIndex of neighbours) {
    if (neighbourIndex < 0 || neighbourIndex >= items.length) {
      continue;
    }

    const item = items[neighbourIndex];

    if (!item || !isPictureItem(item)) {
      continue;
    }

    preloadItems.push(item.data);
  }

  if (preloadItems.length > 0) {
    void preloadImages(preloadItems).catch(() => undefined);
  }
}

useKeydown({
  Escape: () => {
    if (!isOpen.value) {
      return;
    }

    close();
  },
  ArrowLeft: () => {
    if (isOpen.value) {
      goPrev();
    }
  },
  ArrowRight: () => {
    if (isOpen.value) {
      goNext();
    }
  },
});

function getPreviousIndex(): number {
  if (currentIndex.value > 0) {
    return currentIndex.value - 1;
  }

  return loop ? items.length - 1 : currentIndex.value;
}

function getNextIndex(): number {
  if (currentIndex.value < items.length - 1) {
    return currentIndex.value + 1;
  }

  return loop ? 0 : currentIndex.value;
}

type NavigationDirection = -1 | 0 | 1;

function navigateTo(
  index: number,
  direction: NavigationDirection = currentIndex.value === index ? 0 : currentIndex.value < index ? 1 : -1,
): void {
  itemOffset.value = 15 * direction;
  goTo(index);
}

function goPrev(): void {
  const index = getPreviousIndex();

  if (index === currentIndex.value) {
    return;
  }

  navigateTo(index, -1);
}

function goNext(): void {
  const index = getNextIndex();

  if (index === currentIndex.value) {
    return;
  }

  navigateTo(index, 1);
}

defineExpose({
  open,
  close,
  goTo: navigateTo,
  prev: goPrev,
  next: goNext,
  currentIndex,
  isOpen,
  isZoomed,
  resetZoom,
  zoomIn,
  zoomOut,
  zoomStep,
  scalePercent,
  canZoomIn,
  canZoomOut,
});
</script>

<template>
  <teleport :to="teleportTo">
    <transition name="phxsg-lightbox" @after-enter="focusInitialElement">
      <div
        v-if="isOpen"
        :id="`${id}-lightbox`"
        ref="containerRef"
        class="phxsg-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Lightbox"
        tabindex="-1"
        :style="{ '--comp-lightbox-item-enter-offset': `${itemOffset}%` }"
        v-bind="$attrs"
        @keydown="handleLightboxKeydown"
        @pointerdown="handleSwipePointerDown"
        @pointerup="handleSwipePointerUp"
        @pointercancel="resetSwipePointer"
        @touchmove.capture="handleLightboxTouchMove"
        @gesturestart.capture="handleLightboxGesture"
        @gesturechange.capture="handleLightboxGesture"
      >
        <div class="phxsg-lightbox__overlay" @click="handleOverlayClick" />

        <div class="phxsg-lightbox__container">
          <span v-if="counterText" class="phxsg-lightbox__counter">
            {{ counterText }}
          </span>

          <slot
            v-if="isPictureActive"
            name="zoomControl"
            :is-zoomed="isZoomed"
            :toggle-zoom="toggleZoom"
            :reset-zoom="resetZoom"
            :zoom-in="zoomIn"
            :zoom-out="zoomOut"
            :zoom-step="zoomStep"
            :scale-percent="scalePercent"
            :can-zoom-in="canZoomIn"
            :can-zoom-out="canZoomOut"
          />

          <div class="phxsg-lightbox__header">
            <slot name="closeButton" :on-click="close" :class-name="'phxsg-lightbox__close'">
              <button
                type="button"
                class="phxsg-lightbox__close"
                :aria-label="i18n?.close?.label ?? 'Close'"
                @click="close"
              >
                <span class="phxsg-lightbox__close-icon" aria-hidden="true">&times;</span>
              </button>
            </slot>
          </div>

          <div class="phxsg-lightbox__content">
            <transition name="phxsg-lightbox__item-">
              <div v-if="currentItem" :key="`item-${currentIndex}`" class="phxsg-lightbox__item">
                <div
                  v-if="isPictureItem(currentItem)"
                  :class="[
                    'phxsg-lightbox__media',
                    'phxsg-lightbox__media--picture',
                    { 'phxsg-lightbox__media--zoomed': isZoomed },
                  ]"
                >
                  <template v-if="currentPictureData">
                    <slot
                      name="picture"
                      :item="currentItem"
                      :index="currentIndex"
                      :is-zoomed="isZoomed"
                      :toggle-zoom="toggleZoom"
                      :reset-zoom="resetZoom"
                    >
                      <div
                        ref="pictureZoomContainerRef"
                        :class="[
                          'phxsg-lightbox__zoom-container',
                          { 'phxsg-lightbox__zoom-container--off-image': zoomImageOnly && !pointerOnImage },
                        ]"
                        :style="{ cursor: currentCursor }"
                        @click="handlePictureClick"
                        @pointermove="handlePicturePointerMove"
                        @pointerleave="handlePicturePointerLeave"
                      >
                        <div ref="pictureZoomElementRef" class="phxsg-lightbox__zoom-element">
                          <PhxPicture
                            :key="`picture-${currentIndex}`"
                            :alt="currentPictureData.alt"
                            :sources="currentPictureData.sources"
                            :image="currentPictureData.image"
                            img-class="phxsg-lightbox__image"
                            :lazy="false"
                            class="phxsg-lightbox__picture"
                          />
                        </div>
                      </div>
                    </slot>
                  </template>
                  <p
                    v-if="currentPictureData?.caption"
                    :class="['phxsg-lightbox__caption', { 'phxsg-lightbox__caption--hidden': isZoomed }]"
                  >
                    {{ currentPictureData.caption }}
                  </p>
                </div>

                <div
                  v-else-if="isBackgroundVideoItem(currentItem)"
                  class="phxsg-lightbox__media phxsg-lightbox__media--background-video"
                >
                  <slot name="backgroundVideo" :item="currentItem" :index="currentIndex">
                    <PhxsgLightboxBackgroundVideoItem
                      :item="currentItem"
                      :components="components"
                      :i18n="i18n"
                      @mounted="handleBackgroundVideoMounted"
                    />
                  </slot>
                  <p v-if="currentItem.data.caption" class="phxsg-lightbox__caption">
                    {{ currentItem.data.caption }}
                  </p>
                </div>

                <div v-else-if="isVideoItem(currentItem)" class="phxsg-lightbox__media phxsg-lightbox__media--video">
                  <slot name="video" :item="currentItem" :index="currentIndex">
                    <PhxsgLightboxVideoItem :item="currentItem" :components="components" />
                  </slot>
                  <p v-if="currentItem.data.caption" class="phxsg-lightbox__caption">
                    {{ currentItem.data.caption }}
                  </p>
                </div>

                <div v-else class="phxsg-lightbox__media phxsg-lightbox__media--custom">
                  <slot name="customItem" :item="currentItem" :index="currentIndex">
                    <div class="phxsg-lightbox__media-fallback" />
                  </slot>
                </div>
              </div>
            </transition>
          </div>

          <template v-if="items.length > 1">
            <slot
              name="navPrev"
              :on-click="goPrev"
              :disabled="!canGoPrev"
              :class-name="'phxsg-lightbox__nav phxsg-lightbox__nav--prev'"
            >
              <button
                type="button"
                class="phxsg-lightbox__nav phxsg-lightbox__nav--prev"
                :disabled="!canGoPrev"
                :aria-label="i18n?.navPrev?.label ?? 'Previous'"
                @click="goPrev"
              >
                <span class="phxsg-lightbox__nav-icon" aria-hidden="true">&#8249;</span>
              </button>
            </slot>

            <slot
              name="navNext"
              :on-click="goNext"
              :disabled="!canGoNext"
              :class-name="'phxsg-lightbox__nav phxsg-lightbox__nav--next'"
            >
              <button
                type="button"
                class="phxsg-lightbox__nav phxsg-lightbox__nav--next"
                :disabled="!canGoNext"
                :aria-label="i18n?.navNext?.label ?? 'Next'"
                @click="goNext"
              >
                <span class="phxsg-lightbox__nav-icon" aria-hidden="true">&#8250;</span>
              </button>
            </slot>
          </template>

          <div v-if="bullets && items.length > 1" class="phxsg-lightbox__bullets" role="tablist">
            <button
              v-for="(_, bulletIndex) in items"
              :key="bulletIndex"
              type="button"
              role="tab"
              :class="['phxsg-lightbox__bullet', { 'phxsg-lightbox__bullet--active': bulletIndex === currentIndex }]"
              :aria-selected="bulletIndex === currentIndex"
              :aria-label="`${bulletIndex + 1}`"
              @click="navigateTo(bulletIndex)"
            />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style src="./lightbox.css" />
